import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import TopicCloudChart from "../components/TopicCloudChart";
import TopicDistributionChart from "../components/TopicDistributionChart";
import ChannelTopicsTable from "../components/ChannelTopicsTable";
import DominantQuadrantCard from "../components/DominantQuadrantCard";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels,
  generateTopicTrendsData,
  generateChannelTopicsData,
  clearCache
} from "../utils/slackDataService";

// Import report service functions
import {
  fetchReport,
  extractTopicCloudData,
  extractTopicTrendsData,
  extractChannelTopicsData
} from "../utils/slackReportService";

import { shouldBypassSlackConnection } from "../utils/config";

const TopicClustering = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  // Data states
  const [topicCloudData, setTopicCloudData] = useState([]);
  const [topicTrendsData, setTopicTrendsData] = useState([]);
  const [channelTopicsData, setChannelTopicsData] = useState([]);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load Slack data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // If we should bypass, set slackConnected to true and proceed
        if (shouldBypassSlackConnection()) {
          console.log('TopicClustering.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          const channels = await fetchSlackChannels(organization);
          setAvailableChannels(channels);
        } else {
          // Check if Slack is connected
          const isConnected = await checkSlackConnection(organization);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            // Load channels
            const channels = await fetchSlackChannels(organization);
            setAvailableChannels(channels);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  // Load topic data when slackConnected becomes true or when filters change
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      loadTopicData(false);
    }
  }, [slackConnected, organization, initialLoadComplete]);
  
  // Reload data when date range or selected channels change
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      console.log("Filters changed, reloading data:", { dateRange, selectedChannels });
      loadTopicData(true);
    }
  }, [dateRange, selectedChannels, slackConnected, organization, initialLoadComplete]);
  
  // Load topic data based on filters
  const loadTopicData = async (isFilterChange = true, channelsOverride = null) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected");
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      // Force clear the cache when loading fresh data with filters
      clearCache();
      
      // Use channelsOverride if provided, otherwise use selectedChannels state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading topic data with filter:", { 
        dateRange, 
        selectedChannels: channelsToUse,
        isFilterChange 
      });
      
      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      console.log(`Date range: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
      
      // Get channel names to filter by (since backend uses names as keys)
      const channelNames = channelsToUse.length > 0 
        ? channelsToUse
        : null;
      
      // First, fetch the report data from the database
      const reportResult = await fetchReport(organization, {
        channels: channelNames
      });
      
      // Extract topic data from the report
      let cloudData = [];
      let trendsData = [];
      let channelData = [];
      
      if (reportResult && reportResult.success && reportResult.data) {
        console.log("Extracting topic data from report...");
        
        const reportData = reportResult.data.reportData;
        if (reportData && reportData.topics) {
          console.log("Found topics data in report:", Object.keys(reportData.topics));
          
          // Extract data from the report using our new functions
          cloudData = extractTopicCloudData(reportData, startDate.toISOString(), endDate.toISOString());
          trendsData = extractTopicTrendsData(reportData, startDate.toISOString(), endDate.toISOString());
          channelData = extractChannelTopicsData(reportData, startDate.toISOString(), endDate.toISOString());
          
          // Enrich channel data with is_private property
          try {
            const availableChannels = await fetchSlackChannels(organization);
            const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
            channelData = channelData.map(item => ({
              ...item,
              is_private: channelMap.get(item.channel) || false
            }));
          } catch (error) {
            console.warn("Could not fetch channels for is_private property:", error);
          }
          
          console.log("Extracted data:", {
            cloudData: cloudData.length,
            trendsData: trendsData.length,
            channelData: channelData.length
          });
        } else {
          console.log("No topics data found in report");
        }
      } else {
        console.log("No report data available");
        
        // No report data available - return empty data
        console.log("No report data available, returning empty data");
        cloudData = [];
        trendsData = [];
        channelData = [];
      }
      
      console.log("Topic data loaded:", { 
        cloudData: cloudData.length, 
        trendsData: trendsData.length, 
        channelData: channelData.length,
        reportAvailable: !!(reportResult && reportResult.success)
      });
      
      // Check if we have any data
      const hasData = cloudData.length > 0 || trendsData.length > 0 || channelData.length > 0;
      
      if (!hasData) {
        setNoDataAvailable(true);
        // Don't set error message for no data - the EmptyState component handles this
      } else {
        setNoDataAvailable(false);
        setTopicCloudData(cloudData);
        setTopicTrendsData(trendsData);
        setChannelTopicsData(channelData);
      }
      
    } catch (error) {
      console.error("Error loading topic data:", error);
      setError("Error loading topic data. The topic analysis features may still be setting up. Please try again in a moment.");
    } finally {
      if (isFilterChange) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };
  
  // Handle date range change
  const handleDateRangeChange = useCallback((newDateRange) => {
    console.log("Date range changed:", newDateRange);
    
    // Validate the date range before proceeding
    if (!newDateRange || !newDateRange.startDate || !newDateRange.endDate) {
      console.warn("Invalid date range received:", newDateRange);
      return;
    }
    
    setDateRange(newDateRange);
    // Note: Data will be reloaded automatically by useEffect
  }, []);
  
  // Handle channel selection change
  const handleChannelChange = useCallback((channels) => {
    console.log("Channels changed:", channels);
    setSelectedChannels(channels);
    loadTopicData(true, channels); // Pass the new channels directly
  }, []);

  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} organizations={organizations} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading topic clustering data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={organizations} />
      
      <div className="flex-1  overflow-y-auto">
        {/* Header */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Topic clustering</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="bg-grey-20 p-4 rounded-md my-4">
            <p className="text-grey-60">
              You need to connect to Slack first. Please go to the{" "}
              <button 
                onClick={() => navigate('/settings')}
                className="text-brand hover:underline"
              >
                Settings page
              </button>{" "}
              to set up your Slack connection.
            </p>
          </div>
        )}
        
        {/* Error message - only show for actual errors, not for no data scenarios */}
        {error && !noDataAvailable && (
          <div className="bg-red-light p-4 rounded-md my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}
        

        
        {/* Main content - only show if Slack is connected and data is loaded */}
        {slackConnected && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange}
                  onChange={handleDateRangeChange} 
                />
                <ChannelSelector 
                  availableChannels={availableChannels}
                  selectedChannels={selectedChannels}
                  onChange={handleChannelChange} 
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
              </div>
            )}
            
            {/* Charts - side by side layout */}
            <div className="flex flex-row gap-4 p-5 pt-0" style={{ minHeight: "440px" }}>
              {/* Topic Cloud Chart */}
              <div className="flex-1">
                <TopicCloudChart data={topicCloudData} />
              </div>
              <div className="w-[360px] flex flex-col gap-3">
                <DominantQuadrantCard topicCloudData={topicCloudData} />
                <TopicDistributionChart data={topicTrendsData} />
              </div>
            </div>
            
            {/* Channel topics table */}
            <ChannelTopicsTable data={channelTopicsData} />
            
            {/* Empty data message when tables/charts are empty */}
            {(channelTopicsData.length === 0 || noDataAvailable) && !dataLoading && (
              <div className="mt-4">
                <EmptyState
                  variant="no-data"
                  title="No topic data available"
                  description="There are no topic clusters identified for the selected filters. Try selecting a different date range or channels."
                  actionButton={null}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopicClustering; 